import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import dayjs from "dayjs"

import { formatPnr, timeIsInPast } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { createLoadingSelector } from "api"
import { useAppSelector } from "lib/hooks"
import { BookedAppointment } from "reducers/conversations/conversations.reducer"

import { AssignedStaffCell, DateCell, NameCell, NoteTextCell } from "components/v2/conversationList/Cells"
import ReassignDialog from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

import RequestsList from "./RequestsList"

interface BookedAppointmentListItem extends ConversationListItem<BookedAppointment> {}

const LoadingSelector = createLoadingSelector(["CLINIC_CONVERSATIONS"])

interface BookedAppointmentsListProps {
  conversations: BookedAppointment[]
  requests: BookedAppointment[]
}

const BookedAppointmentsList = ({ conversations, requests }: BookedAppointmentsListProps) => {
  const intl = useIntl()
  const kinds = useAppSelector(state => state.booking.bookingTypes)
  const appLanguage = useAppSelector(state => state.app.language)
  const loader = useAppSelector(state => LoadingSelector(state))
  const { activeStaff } = useAppSelector(state => state.users)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<BookedAppointment>()
  const user = useAppSelector(state => state.auth.user)

  const openReassignDialog = (item: BookedAppointmentListItem) => {
    setSelectedConversation(item.conversation)
    setReassignDialogOpen(true)
  }

  const closeReassignDialog = () => {
    setSelectedConversation(undefined)
    setReassignDialogOpen(false)
  }

  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "kind",
      name: intl.formatMessage({ id: "label.overview.booking_type" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.booked_time" })
    },
    {
      key: "assigned",
      name: intl.formatMessage({ id: "label.overview.assigned" })
    },
    {
      key: "comment",
      name: intl.formatMessage({ id: "label.overview.comment" })
    }
  ]
  const requestsColumns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "status",
      name: intl.formatMessage({ id: "label.overview.status" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.booked_time" })
    },
    {
      key: "comment",
      name: intl.formatMessage({ id: "label.overview.comment" })
    }
  ]
  const shouldNotify = ({ bookingStart, assignedStaffId }: BookedAppointment) =>
    dayjs(bookingStart).isSame(dayjs(), "day") || (timeIsInPast(bookingStart) && !assignedStaffId)

  const mapConversationListItem = useCallback(
    (conv: BookedAppointment): BookedAppointmentListItem => {
      const isChildConversation = conv.profile?.type === "child"
      const { kind_id: kindId } = JSON.parse(conv.bookingFilter)
      const isPast = shouldNotify(conv)
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          status: intl.formatMessage({
            id:
              conv.reassignTo === user?.id
                ? "label.requests.status.waiting_for_you"
                : "label.requests.status.waiting_for_colleague"
          }),
          kind: kinds.find(k => k.id === kindId)?.localisedNames[appLanguage],
          start: <DateCell past={isPast} value={conv.bookingStart} />,
          assigned: <AssignedStaffCell staff={activeStaff} staffId={conv.assignedStaffId} />,
          comment: <NoteTextCell conversation={conv} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "assigned"], {}),
          kind: sortValueFunctions.bookingType(conv, { bookingTypes: kinds, appLanguage }),
          start: sortValueFunctions.bookingStart(conv)
        }
      }
    },
    [activeStaff, appLanguage, kinds, user?.id, intl]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const requestsList = useMemo(
    () => requests?.map(conv => mapConversationListItem(conv)) || [],
    [requests, mapConversationListItem]
  )

  const shouldNotifyItem = (item: BookedAppointmentListItem) => {
    return shouldNotify(item.conversation)
  }

  return (
    <>
      {requests && requests.length > 0 && <RequestsList columns={requestsColumns} items={requestsList} />}
      <ConversationTable
        title={intl.formatMessage({ id: "label.overview.booked_appointments" })}
        columns={columns}
        items={list}
        emptyMessage={intl.formatMessage({ id: "label.overview.list_empty" })}
        onItemClick={openReassignDialog}
        shouldNotify={shouldNotifyItem}
        loading={loader}
      />
      {reassignDialogOpen && selectedConversation && (
        <ReassignDialog
          isOpen={reassignDialogOpen}
          conversation={selectedConversation!}
          onClose={closeReassignDialog}
        />
      )}
    </>
  )
}

export default BookedAppointmentsList
